import { Box, useTheme } from '@mui/system';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import React from 'react';
import { Line } from 'react-chartjs-2';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { ChartLegendItem } from './ChartLegendItem';

type LineChartDataset = ChartDataset<'line'> & { id: string; label?: string; borderColor: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
    const result = { ...target };
    for (const key of Object.keys(source) as (keyof T)[]) {
        const sourceVal = source[key];
        const targetVal = target[key];
        if (
            sourceVal &&
            typeof sourceVal === 'object' &&
            !Array.isArray(sourceVal) &&
            targetVal &&
            typeof targetVal === 'object' &&
            !Array.isArray(targetVal)
        ) {
            result[key] = deepMerge(targetVal, sourceVal);
        } else if (sourceVal !== undefined) {
            result[key] = sourceVal as T[keyof T];
        }
    }
    return result;
}

interface LineChartProps {
    chartData: Omit<ChartData<'line'>, 'datasets'> & { datasets: LineChartDataset[] };
    chartOptions?: ChartOptions<'line'>;
    showCustomLegend?: boolean;
    height?: number;
}

export function LineChart({
    chartData,
    chartOptions: chartOptionOverrides,
    showCustomLegend = true,
    height = 400
}: LineChartProps) {
    const theme = useTheme();

    const defaultOptions = React.useMemo<ChartOptions<'line'>>(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index' as const,
                intersect: false
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: true,
                    backgroundColor: theme.palette.background.paper,
                    titleColor: theme.palette.text.primary,
                    bodyColor: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    borderWidth: 1,
                    padding: 12,
                    usePointStyle: true,
                    position: 'nearest' as const
                }
            },
            scales: {
                x: {
                    grid: { color: theme.palette.divider, borderDash: [3, 3] },
                    border: { display: false, dash: [3, 3] },
                    ticks: {
                        font: { size: 11 },
                        color: theme.palette.text.secondary,
                        maxRotation: 0
                    }
                },
                y: {
                    beginAtZero: true,
                    grid: { color: theme.palette.divider, borderDash: [3, 3] },
                    border: { display: false, dash: [3, 3] },
                    ticks: {
                        font: { size: 11 },
                        color: theme.palette.text.secondary
                    }
                }
            }
        }),
        [theme]
    );

    const mergedOptions = React.useMemo(
        () => (chartOptionOverrides ? deepMerge(defaultOptions, chartOptionOverrides) : defaultOptions),
        [defaultOptions, chartOptionOverrides]
    );

    const crosshairPlugin = React.useMemo(
        () => ({
            id: 'crosshairLine',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            beforeDatasetsDraw(chart: any) {
                const activeElements = chart.tooltip?._active;
                if (activeElements?.length) {
                    const { ctx, chartArea } = chart;
                    const x = activeElements[0].element.x;
                    ctx.save();
                    ctx.beginPath();
                    ctx.setLineDash([]);
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = theme.palette.grey[500];
                    ctx.moveTo(x, chartArea.top);
                    ctx.lineTo(x, chartArea.bottom);
                    ctx.stroke();
                    ctx.restore();
                }
            }
        }),
        [theme]
    );

    const chartRef = React.useRef<ChartJSOrUndefined<'line'>>(null);
    const [hiddenDatasets, setHiddenDatasets] = React.useState<Record<string, boolean>>({});

    const toggleDataset = React.useCallback((id: string) => {
        const chart = chartRef.current;
        if (!chart) {
            return;
        }
        const datasetIndex = chart.data.datasets.findIndex(
            ds => (ds as unknown as { id?: string }).id === id
        );
        if (datasetIndex === -1) {
            return;
        }
        const isHidden = !chart.isDatasetVisible(datasetIndex);
        chart.setDatasetVisibility(datasetIndex, isHidden);
        chart.update();
        setHiddenDatasets(prev => ({ ...prev, [id]: !isHidden }));
    }, []);

    return (
        <>
            <Box height={height} position="relative">
                <Line ref={chartRef} data={chartData} options={mergedOptions} plugins={[crosshairPlugin]} />
            </Box>
            {showCustomLegend && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        mt: 1
                    }}
                >
                    {chartData.datasets.map(dataset => (
                        <ChartLegendItem
                            key={dataset.id}
                            id={dataset.id}
                            label={dataset.label}
                            color={dataset.borderColor}
                            hidden={!!hiddenDatasets[dataset.id]}
                            onToggle={toggleDataset}
                        />
                    ))}
                </Box>
            )}
        </>
    );
}
